/**
 * Renders the floor plan layout image carousel shared by floorPlanDetail's
 * [data-secondary-panel] (desktop) and mobile image-expand screen — a plan can have more than one
 * floor plan image (e.g. one per level), listed under the selected plan's `floorplans` array (see
 * redux/STORE_DESIGN.md). Wraps components/carousel/carousel.js (Glider.js) for the arrows +
 * drag-to-scroll; native touch scrolling (from the structural `data-floor-plan-carousel*` rules in
 * _components.native-interactive-site-map.scss) covers mobile swipe without extra JS.
 *
 * Pure render function — matching this folder's existing .dom.js convention (see
 * floorPlanImage.dom.js) — plus a separate initialize step, since Glider.js needs the slide
 * elements to already exist in the live DOM before it can bind to them. Callers must call
 * initializeFloorPlanCarousel() immediately after inserting the rendered markup (e.g. right after
 * an `element.innerHTML = renderFloorPlanCarousel(...)` assignment), so the querySelector calls
 * inside carousel.js resolve against the new nodes.
 *
 * Only one floor plan carousel is ever mounted at a time (desktop's secondary panel and mobile's
 * floorPlanViewer are mutually exclusive — see floorPlanDetail.plugin.js), so the fixed, global
 * data-attribute selectors below are safe: carousel.js's own API takes selector strings, not
 * elements, and resolves them via document.querySelector.
 *
 * @module nativeInteractiveSiteMap/components/floorPlanCarousel.dom
 */
import Carousel from '@components/carousel/carousel';
import { carouselArrowIconSvg } from 'constants/svg-assets';
import renderFloorPlanImage from './floorPlanImage.dom';

const CAROUSEL_SELECTOR = '[data-floor-plan-carousel]';
const PREV_ARROW_SELECTOR = '[data-floor-plan-carousel-prev]';
const NEXT_ARROW_SELECTOR = '[data-floor-plan-carousel-next]';

const ARROW_CLASS = 'nhs-c-native-interactive-site-map__carousel-arrow';

/**
 * Derives the accessible alt text for one floor plan image from its own title/description —
 * unlike floorPlanImage.dom.js's deriveFloorPlanImageAlt (which describes the whole plan), each
 * image here can represent a different level/area of the same plan, so the alt text has to come
 * from the image itself.
 * @param {object|null} image - A single entry from a plan's `floorplans` array.
 * @returns {string}
 */
export function deriveFloorPlanCarouselImageAlt(image) {
  if (!image) {
    return 'floor plan layout image';
  }
  if (image.title && image.description) {
    return `${image.title} - ${image.description}`;
  }
  return image.title || image.description || 'floor plan layout image';
}

/**
 * @param {object} params
 * @param {object[]} [params.floorplans] - The selected plan's `floorplans` array
 *   ({ title, url, description, displayOrder }), unsorted.
 * @returns {string} HTML string.
 */
export default function renderFloorPlanCarousel({ floorplans } = {}) {
  const sortedImages = [...(floorplans ?? [])].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );

  if (sortedImages.length === 0) {
    return renderFloorPlanImage({ imageUrl: null, imageAlt: deriveFloorPlanCarouselImageAlt(null) });
  }

  const slidesHtml = sortedImages
    .map((image) => `
      <div class="glider-slide" data-floor-plan-carousel-slide>
        ${renderFloorPlanImage({ imageUrl: image.url, imageAlt: deriveFloorPlanCarouselImageAlt(image) })}
      </div>
    `)
    .join('');

  return `
    <div class="nhs-c-native-interactive-site-map__floor-plan-carousel" data-qa="native_ism_floor_plan_carousel">
      <div class="glider" data-floor-plan-carousel>${slidesHtml}</div>
      <button type="button"
              class="${ARROW_CLASS} ${ARROW_CLASS}--prev"
              aria-label="Previous floor plan image"
              data-floor-plan-carousel-prev
              data-qa="native_ism_floor_plan_carousel_prev">
        ${carouselArrowIconSvg}
      </button>
      <button type="button"
              class="${ARROW_CLASS} ${ARROW_CLASS}--next"
              aria-label="Next floor plan image"
              data-floor-plan-carousel-next
              data-qa="native_ism_floor_plan_carousel_next">
        ${carouselArrowIconSvg}
      </button>
    </div>
  `;
}

/**
 * Binds carousel.js (Glider.js) to the markup rendered by renderFloorPlanCarousel(). Must be
 * called synchronously right after that markup is inserted into the live DOM. A no-op when there
 * is no carousel to bind to (the zero-images fallback renders a plain image, not `.glider`).
 *
 * Glider.js wraps the slides in a track `<div>` of its own creation (no class/attribute hook we
 * author up front — see components/carousel/carousel.js), synchronously during `.create()`. Right
 * after `.create()` returns, that track is the carousel container's only child, so it's tagged
 * here with `data-floor-plan-carousel-track` to give _components.native-interactive-site-map.scss
 * a selector it controls instead of styling off Glider's own `.glider-track` class.
 */
export function initializeFloorPlanCarousel() {
  const container = document.querySelector(CAROUSEL_SELECTOR);
  if (!container) {
    return;
  }

  new Carousel()
    .setContainer(CAROUSEL_SELECTOR)
    .setSlidesToShow(1)
    .setDraggable(true)
    .setLeftArrow(PREV_ARROW_SELECTOR)
    .setRightArrow(NEXT_ARROW_SELECTOR)
    .create()
    .hideDisabledArrows();

  container.firstElementChild?.setAttribute('data-floor-plan-carousel-track', '');
}
